/**
 * ---------------------------------------------------------------------------------------------------------------------
 * SOLD OUT ALTERNATIVES (RICHROYAL-201F)
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Fills the "Wie wäre es alternativ hiermit:" slider on the PDP (`snippets/sold-out-alternatives.liquid`).
 *
 * The module is shown only while the selected variant is sold out. When that is the case it walks the collection
 * levels handed over by Liquid — most specific first ("Midiröcke"), then the broader one ("Röcke") — and asks
 * `sections/alternative-products.liquid` for tiles per level:
 *
 *     /collections/<handle>?filter.v.option.color=<colour>&filter.v.option.size=<size>&filter.v.availability=1
 *       &section_id=alternative-products
 *
 * Two details drive the shape of the code:
 *
 *   1. Storefront filter values are matched EXACTLY, including case ("caffee latte" hits, "Caffee Latte" does
 *      not), and repeating a filter parameter ORs its values. So the values sent out keep the casing they were
 *      authored with, while the client-side comparison works on a lowercased copy.
 *
 *   2. `filter.v.option.size` + `filter.v.availability` are NOT variant-accurate together: they return products
 *      that have the size AND have something in stock, not products that have that size in stock. The section
 *      therefore also returns which sizes are really in stock per product, and every tile is re-checked against
 *      that here before it is allowed on screen. This is the ticket's "never suggest a product that is also sold
 *      out in the size the customer needs".
 */

const SECTION_ID = 'alternative-products';

/**
 * Parts of the shared product tile that this slider does not show. `product-card` gates the wishlist heart only
 * behind `hide_product_information`, which would take the title and price with it, so the nodes are dropped from
 * the fetched fragment instead. Dropping them here — while the fragment still belongs to the inert DOMParser
 * document — means <wish-heart> is never constructed and never registers with the wishlist API.
 */
const STRIPPED_FROM_TILE = ['wish-heart', '.add-to-wish-banner'];

/**
 * Tiles that fit the row on desktop — kept in sync with the `/ 3` in the snippet's card-width rule. It decides
 * both how far one arrow click moves the row and, per the ticket's "more than 3 products returned", whether the
 * arrows are shown at all.
 */
const TILES_PER_ROW_DESKTOP = 3;

/** Level responses are immutable for the lifetime of the page — share them across levels and re-selections. */
const responseCache = new Map();

const normalize = (value) => (value || '').toString().trim().toLowerCase();

/** Matches how the theme picks its own scroll behaviour, so the arrows honour a reduced-motion preference. */
const scrollBehavior = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

/** Parses the "a, b; c, d" attribute the snippet builds out of a multi-line theme setting. */
const parseGroups = (value) =>
  (value || '')
    .split(';')
    .map((group) =>
      group
        .split(',')
        .map((member) => member.trim())
        .filter(Boolean)
    )
    .filter((group) => group.length > 0);

class SoldOutAlternatives extends HTMLElement {
  #abortController;
  #renderToken = 0;
  #arrowObserver;

  connectedCallback() {
    this.#abortController = new AbortController();

    document.addEventListener('variant:change', this.#onVariantChange, { signal: this.#abortController.signal });

    this.#update({
      color: this.dataset.initialColor,
      size: this.dataset.initialSize,
      available: this.dataset.initialAvailable === 'true',
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#arrowObserver?.disconnect();
  }

  get collections() {
    return (this.dataset.collections || '').split(',').filter(Boolean);
  }

  get limit() {
    return parseInt(this.dataset.limit, 10) || 5;
  }

  #onVariantChange = (event) => {
    // `event.detail.formId` cannot be used: the theme fills it with `form.id`, and that is not the form's id
    // string. HTMLFormElement's named getter overrides built-in properties, and the product form contains
    // <input name="id">, so `form.id` resolves to that input element (which is exactly why the theme's own code
    // can call `form.id.value = ...`). The event bubbles from the form, so take the id off the target instead —
    // with getAttribute, for the same reason.
    const dispatchingFormId = event.target?.getAttribute?.('id');

    // Quick buy modals and featured products dispatch the same event; only the form this block belongs to counts.
    if (this.dataset.formId && dispatchingFormId && dispatchingFormId !== this.dataset.formId) {
      return;
    }

    const variant = event.detail?.variant;

    // An option combination that does not exist at all resolves to an empty variant — nothing to suggest for.
    if (!variant?.id) {
      this.#hide();
      return;
    }

    this.#update({
      color: this.#optionValue(variant, this.dataset.colorPosition),
      size: this.#optionValue(variant, this.dataset.sizePosition),
      available: variant.available !== false,
    });
  };

  #optionValue(variant, position) {
    const index = parseInt(position, 10) - 1;

    if (!Number.isInteger(index) || index < 0) {
      return '';
    }

    return variant.options?.[index] || '';
  }

  /**
   * Expands one option value into every value worth filtering on. `send` keeps the authored casing because the
   * storefront filter is case sensitive, `match` is the lowercased copy used against the section's response.
   */
  #expand(value, groups) {
    const send = new Map();
    const match = new Set();

    // Labels are only ever compared as a whole. Splitting them apart looks tempting for "36/38", but Rich&Royal
    // denim is sized "25/32" (waist/length), where the halves are not sizes of their own — a "25/32" customer
    // must not be answered with something that merely has a 32 in stock. Combined labels belong in a group line.
    const add = (candidate) => {
      const label = (candidate || '').toString().trim();

      if (!label) {
        return;
      }

      const key = normalize(label);

      if (!send.has(key)) {
        send.set(key, label);
      }

      match.add(key);
    };

    add(value);

    // A group applies as soon as one of its members is the selected value — then all of its members join in.
    groups.forEach((group) => {
      if (group.some((member) => match.has(normalize(member)))) {
        group.forEach(add);
      }
    });

    return { send: [...send.values()], match: [...match] };
  }

  async #update({ color, size, available }) {
    // In-flight levels are not aborted, they are outrun: a superseded pass drops out at the token check below
    // and its response stays in the cache, so flipping back and forth between sizes costs nothing.
    const token = ++this.#renderToken;

    if (available) {
      this.#hide();
      return;
    }

    const collections = this.collections;

    if (collections.length === 0) {
      this.#hide();
      return;
    }

    const colors = this.#expand(color, parseGroups(this.dataset.colorGroups));
    const sizes = this.#expand(size, parseGroups(this.dataset.sizeGroups));

    const cards = [];
    const seen = new Set([this.dataset.productHandle]);

    const passes = collections.map((collection) => ({ collection, withColor: true }));

    // Optional last resort: the broadest collection once more, this time without the colour filter. Off by
    // default — the colour is requirement 3 of the ticket, and dropping it is a merchandising call, not a
    // technical one. It matters because the exact colour is by far the narrowest of the four criteria.
    if (this.dataset.ignoreColorOnLastLevel === 'true' && collections.length > 0) {
      passes.push({ collection: collections[collections.length - 1], withColor: false });
    }

    for (const { collection, withColor } of passes) {
      let level;

      try {
        level = await this.#fetchLevel(collection, withColor ? colors.send : [], sizes.send);
      } catch (error) {
        console.error(`Could not load sold-out alternatives from "${collection}".`, error);
        continue;
      }

      // A newer selection started while this level was in flight.
      if (token !== this.#renderToken) {
        return;
      }

      for (const { handle, card } of level.cards) {
        if (seen.has(handle) || !this.#isInStock(handle, level.sizesByHandle, sizes.match)) {
          continue;
        }

        seen.add(handle);
        cards.push(card);

        if (cards.length >= this.limit) {
          break;
        }
      }

      if (cards.length >= this.limit) {
        break;
      }
    }

    // Every level may have failed before a token check was reached, so re-check before touching the DOM.
    if (token !== this.#renderToken) {
      return;
    }

    if (cards.length === 0) {
      this.#hide();
      return;
    }

    this.#render(cards);
  }

  #fetchLevel(collection, colors, sizes) {
    const root = window.Shopify?.routes?.root || '/';
    const params = [
      `section_id=${SECTION_ID}`,
      'filter.v.availability=1',
      ...(this.dataset.colorParam ? colors.map((value) => `${this.dataset.colorParam}=${encodeURIComponent(value)}`) : []),
      ...(this.dataset.sizeParam ? sizes.map((value) => `${this.dataset.sizeParam}=${encodeURIComponent(value)}`) : []),
    ];

    const url = `${root}collections/${encodeURIComponent(collection)}?${params.join('&')}`;

    if (!responseCache.has(url)) {
      responseCache.set(
        url,
        fetch(url)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`${response.status} ${response.statusText}`);
            }

            return response.text();
          })
          .then((html) => this.#parseLevel(html))
          .catch((error) => {
            // Never cache a failure — a failed level has to be retried on the next selection.
            responseCache.delete(url);
            throw error;
          })
      );
    }

    return responseCache.get(url);
  }

  #parseLevel(html) {
    const document_ = new DOMParser().parseFromString(html, 'text/html');
    const results = document_.querySelector('[data-alternative-products]');
    const sizesElement = document_.querySelector('[data-alternative-products-sizes]');

    let sizesByHandle = {};

    try {
      sizesByHandle = JSON.parse(sizesElement?.textContent || '{}');
    } catch (error) {
      console.error('Could not read the availability map of the alternative products section.', error);
    }

    const cards = Array.from(results?.querySelectorAll('product-card') || []).map((card) => {
      STRIPPED_FROM_TILE.forEach((selector) => card.querySelectorAll(selector).forEach((node) => node.remove()));

      return {
        // `product-card` carries "<handle>?variant=<id>" — the plain handle keys the availability map.
        handle: (card.getAttribute('handle') || '').split('?')[0],
        card,
      };
    });

    return { cards, sizesByHandle };
  }

  #isInStock(handle, sizesByHandle, sizes) {
    const availableSizes = sizesByHandle[handle];

    // Outside the rendered window, so its stock could not be verified — leaving it out is the safe read.
    if (typeof availableSizes !== 'string' || availableSizes === '') {
      return false;
    }

    // The alternative has no size axis of its own, or the current product has none to match against.
    if (availableSizes === '*' || sizes.length === 0) {
      return true;
    }

    return sizes.some((size) => availableSizes.includes(`|${size}|`));
  }

  #render(cards) {
    const output = this.querySelector('[data-alternatives-output]');
    const template = this.querySelector('[data-alternatives-template]');

    if (!output || !template) {
      return;
    }

    const shell = template.content.cloneNode(true);
    const carousel = shell.querySelector('[data-alternatives-carousel]');

    if (!carousel) {
      return;
    }

    cards.forEach((card) => carousel.appendChild(document.importNode(card, true)));

    this.classList.toggle('sold-out-alternatives--slidable', cards.length > TILES_PER_ROW_DESKTOP);

    // Unhide before inserting so the row is measurable straight away — the arrow states are read off its
    // scroll offsets, and a hidden module measures 0.
    this.hidden = false;
    output.replaceChildren(shell);
    this.#wireArrows();
  }

  /**
   * The row is a plain overflow container, so the two arrows are driven from here: they move it by whole tiles
   * and their disabled state is read off the actual scroll offset. Deriving it from the offset rather than from
   * edge events means it stays correct however the row was moved — arrow, touch swipe or trackpad.
   */
  #wireArrows() {
    const row = this.querySelector('[data-alternatives-carousel]');
    const previous = this.querySelector('[data-alternatives-prev]');
    const next = this.querySelector('[data-alternatives-next]');

    if (!row || !previous || !next) {
      return;
    }

    const { signal } = this.#abortController;
    const furthest = () => row.scrollWidth - row.clientWidth;

    // A pixel of slack: fractional column widths mean the offset rarely lands on a whole number.
    const syncTo = (offset) => {
      previous.disabled = offset <= 1;
      next.disabled = offset >= furthest() - 1;
    };

    const sync = () => syncTo(row.scrollLeft);

    // One page is the three tiles the row shows, measured from the real tile pitch so it stays right at every
    // breakpoint.
    const page = () => {
      const tiles = row.children;
      const pitch =
        tiles.length > 1 ? tiles[1].offsetLeft - tiles[0].offsetLeft : tiles[0]?.offsetWidth || row.clientWidth;

      return (pitch || row.clientWidth) * TILES_PER_ROW_DESKTOP;
    };

    // The target is clamped here rather than left to the browser so the buttons can be updated straight away:
    // waiting for the scroll event would leave them a whole smooth-scroll animation behind, and the last page is
    // always a partial one (three tiles stay visible, so the end offset is never a whole number of tiles).
    const move = (direction) => {
      const target = Math.min(Math.max(row.scrollLeft + direction * page(), 0), furthest());

      row.scrollTo({ left: target, behavior: scrollBehavior() });
      syncTo(target);
    };

    previous.addEventListener('click', () => move(-1), { signal });
    next.addEventListener('click', () => move(1), { signal });

    // Keeps the buttons right for every other way the row can move: touch swipe, trackpad, shift-wheel.
    row.addEventListener('scroll', sync, { passive: true, signal });

    sync();

    // Re-derive the state whenever the row is resized: a viewport change changes the tile width, and with it
    // how far the row can scroll.
    this.#arrowObserver?.disconnect();
    this.#arrowObserver = new ResizeObserver(sync);
    this.#arrowObserver.observe(row);
  }

  #hide() {
    this.hidden = true;
    this.classList.remove('sold-out-alternatives--slidable');
    this.#arrowObserver?.disconnect();
    this.querySelector('[data-alternatives-output]')?.replaceChildren();
  }
}

if (!window.customElements.get('sold-out-alternatives')) {
  window.customElements.define('sold-out-alternatives', SoldOutAlternatives);
}
